/**
 * Pure jsPDF Invoice Generator
 * Note: Arabic language will show in French labels until custom fonts are added
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface InvoiceData {
  invoice_number: string;
  created_at: string;
  due_date?: string;
  status: string;
  clients: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    wilaya?: string;
    tax_id?: string;
    nrc?: string;
    nis?: string;
    article_number?: string;
  };
  invoice_items: Array<{
    products: { name: string; code?: string };
    quantity: number;
    unit: string;
    unit_price: number;
    vat: number;
    discount?: number;
    discount_type?: string;
    total: number;
    description?: string;
  }>;
  subtotal: number;
  tax_amount: number;
  discount?: number;
  stamp_duty?: number;
  total: number;
  paid_amount?: number;
  notes?: string;
  payment_terms?: string;
}

export type InvoiceLanguage = 'ar' | 'fr';

/**
 * Format number for Algerian invoices
 */
function formatAlgerianNumber(value: number): string {
  const parts = value.toFixed(2).split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedInteger},${decimalPart}`;
}

/**
 * Format date in dd/mm/yyyy format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Get translations based on language
 * Note: For now, both languages use French labels
 * Arabic support requires custom font files
 */
function getTranslations(lang: InvoiceLanguage) {
  // Using French labels for both until Arabic fonts are added
  return {
    invoice: 'FACTURE',
    invoiceNumber: 'N°',
    date: 'Date',
    dueDate: 'Échéance',
    client: 'CLIENT',
    code: 'Code',
    designation: 'Désignation',
    quantity: 'Qté',
    unitPrice: 'P.U. HT',
    vat: 'TVA',
    discount: 'Remise',
    total: 'Total',
    subtotal: 'Sous-total HT',
    stampDuty: 'Timbre fiscal',
    totalTTC: 'Total TTC',
    paid: 'Payé',
    remaining: 'Reste à payer',
    notes: 'Notes',
    paymentTerms: 'Conditions de paiement',
    thanks: 'Merci pour votre confiance',
    companyInfo: {
      name: 'Votre Entreprise',
      address: 'Adresse de l\'entreprise',
      phone: 'Tél: 0XX XX XX XX XX',
      nif: 'NIF: XXXXXXXXXXXXXX',
    }
  };
}

/**
 * Generate invoice PDF using pure jsPDF
 */
export function generateInvoicePDF(invoice: InvoiceData, language: InvoiceLanguage = 'fr') {
  const t = getTranslations(language);
  
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let yPos = 20;

  // ========== HEADER ==========
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text(t.invoice, pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 15;

  // ========== COMPANY INFO (Left) & INVOICE INFO (Right) ==========
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  // Left side - Company info
  doc.text(t.companyInfo.name, 20, yPos);
  doc.text(t.companyInfo.address, 20, yPos + 5);
  doc.text(t.companyInfo.phone, 20, yPos + 10);
  doc.text(t.companyInfo.nif, 20, yPos + 15);
  
  // Right side - Invoice info
  doc.setFont('helvetica', 'bold');
  doc.text(`${t.invoiceNumber} ${invoice.invoice_number}`, pageWidth - 20, yPos, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.text(`${t.date}: ${formatDate(invoice.created_at)}`, pageWidth - 20, yPos + 5, { align: 'right' });
  if (invoice.due_date) {
    doc.text(`${t.dueDate}: ${formatDate(invoice.due_date)}`, pageWidth - 20, yPos + 10, { align: 'right' });
  }
  
  yPos += 30;

  // ========== CLIENT INFO ==========
  doc.setFillColor(245, 245, 245);
  const clientBoxHeight = 25;
  doc.rect(20, yPos, pageWidth - 40, clientBoxHeight, 'F');
  
  yPos += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(t.client, 25, yPos);
  
  yPos += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(invoice.clients.name, 25, yPos);
  
  yPos += 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  
  if (invoice.clients.address) {
    doc.text(invoice.clients.address, 25, yPos);
    yPos += 4;
  }
  
  const contactParts = [];
  if (invoice.clients.phone) contactParts.push(`Tél: ${invoice.clients.phone}`);
  if (invoice.clients.email) contactParts.push(invoice.clients.email);
  if (contactParts.length > 0) {
    doc.text(contactParts.join(' | '), 25, yPos);
  }
  
  yPos = yPos + clientBoxHeight - 10;

  // ========== ITEMS TABLE ==========
  const hasDiscounts = invoice.invoice_items.some(item => item.discount && item.discount > 0);
  
  const headers = [t.code, t.designation, t.quantity, t.unitPrice, t.vat];
  if (hasDiscounts) headers.push(t.discount);
  headers.push(t.total);

  const tableData = invoice.invoice_items.map(item => {
    const row = [
      item.products.code || '-',
      item.products.name + (item.description ? `\n${item.description}` : ''),
      `${item.quantity} ${item.unit}`,
      `${formatAlgerianNumber(item.unit_price)} DA`,
      `${item.vat}%`,
    ];
    
    if (hasDiscounts) {
      row.push(
        item.discount && item.discount > 0
          ? item.discount_type === 'percentage'
            ? `${item.discount}%`
            : `${formatAlgerianNumber(item.discount)} DA`
          : '-'
      );
    }
    
    row.push(`${formatAlgerianNumber(item.total)} DA`);
    return row;
  });

  autoTable(doc, {
    startY: yPos,
    head: [headers],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: 255,
      fontSize: 9,
      fontStyle: 'bold',
      halign: 'center',
    },
    bodyStyles: {
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 30, halign: 'right' },
      4: { cellWidth: 15, halign: 'center' },
      ...(hasDiscounts && { 5: { cellWidth: 25, halign: 'right' } }),
    },
    margin: { left: 20, right: 20 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // ========== NOTES ==========
  if (invoice.notes) {
    doc.setFillColor(255, 251, 235);
    const notesHeight = Math.min(20, 10 + (invoice.notes.length / 50) * 4);
    doc.rect(20, yPos, (pageWidth - 40) * 0.65, notesHeight, 'F');
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`${t.notes}:`, 25, yPos + 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    
    const splitNotes = doc.splitTextToSize(invoice.notes, (pageWidth - 50) * 0.65);
    doc.text(splitNotes, 25, yPos + 10);
    
    yPos += notesHeight + 5;
  }

  // ========== TOTALS (Right side) ==========
  const totalsX = pageWidth - 75;
  let totalsY = (doc as any).lastAutoTable.finalY + 10;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  // Subtotal
  doc.text(`${t.subtotal}:`, totalsX, totalsY);
  doc.text(`${formatAlgerianNumber(invoice.subtotal || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
  totalsY += 6;
  
  // Discount
  if (invoice.discount && invoice.discount > 0) {
    doc.setTextColor(220, 38, 38);
    doc.text(`${t.discount}:`, totalsX, totalsY);
    doc.text(`-${formatAlgerianNumber(invoice.discount || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    totalsY += 6;
  }
  
  // TVA
  doc.text(`${t.vat}:`, totalsX, totalsY);
  doc.text(`${formatAlgerianNumber(invoice.tax_amount || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
  totalsY += 6;
  
  // Stamp duty
  if (invoice.stamp_duty && invoice.stamp_duty > 0) {
    doc.text(`${t.stampDuty}:`, totalsX, totalsY);
    doc.text(`${formatAlgerianNumber(invoice.stamp_duty || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    totalsY += 6;
  }
  
  // Total line
  doc.setLineWidth(0.5);
  doc.line(totalsX, totalsY, pageWidth - 20, totalsY);
  totalsY += 8;
  
  // Total TTC
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(16, 185, 129);
  doc.text(`${t.totalTTC}:`, totalsX, totalsY);
  doc.text(`${formatAlgerianNumber(invoice.total || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
  doc.setTextColor(0, 0, 0);
  
  // Paid amount & Remaining
  if (invoice.paid_amount && invoice.paid_amount > 0) {
    totalsY += 9;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(5, 150, 105);
    doc.text(`${t.paid}:`, totalsX, totalsY);
    doc.text(`${formatAlgerianNumber(invoice.paid_amount || 0)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    
    const remaining = (invoice.total || 0) - (invoice.paid_amount || 0);
    if (remaining > 0) {
      totalsY += 6;
      doc.setTextColor(239, 68, 68);
      doc.text(`${t.remaining}:`, totalsX, totalsY);
      doc.text(`${formatAlgerianNumber(remaining)} DA`, pageWidth - 20, totalsY, { align: 'right' });
    }
    doc.setTextColor(0, 0, 0);
  }

  // ========== PAYMENT TERMS ==========
  if (invoice.payment_terms) {
    const termsY = Math.max(totalsY + 15, pageHeight - 40);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(100, 100, 100);
    const splitTerms = doc.splitTextToSize(`${t.paymentTerms}: ${invoice.payment_terms}`, pageWidth - 40);
    doc.text(splitTerms, 20, termsY);
    doc.setTextColor(0, 0, 0);
  }

  // ========== FOOTER ==========
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(128, 128, 128);
  doc.text(t.thanks, pageWidth / 2, pageHeight - 15, { align: 'center' });
  doc.setFontSize(7);
  doc.text(`Générée le ${formatDate(new Date().toISOString())}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

  // ========== SAVE ==========
  doc.save(`Facture-${invoice.invoice_number}-${language.toUpperCase()}.pdf`);
}
